import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useSupplierMitigationTasks } from '../../hooks/useSupplierMitigationTasks'
import { useSuppliers } from '../../hooks/useSuppliers'
import type { SupplierMitigationTask } from '../../types'
import { RiskScaleLegend } from './RiskScaleLegend'
import {
  bandCountsUnique,
  collapsedBand,
  collapsedCategoryKey,
  collapsedMatchesLocation,
  collapsedRegionKey,
  collapsedSubCategoryKey,
  collapseSuppliersForPortfolio,
  formatBrandsDisplay,
  formatBrandsFull,
  rollupCollapsedByAttribute,
  rollupCollapsedByBrand,
  rollupCollapsedByLocation,
  type CollapsedSupplierRisk,
  type RollupRow,
} from './supplierRollup'
import {
  formatExclusivePercents,
  formatMetric,
  MetricModeToggle,
  type MetricMode,
} from './MetricModeToggle'
import { RiskBandChart, RollupBarsChart } from './RiskCharts'
import { toErrorMessage } from '../../utils/toErrorMessage'
import { CopyTableButtons } from './CopyTableButtons'
import { cell } from './copyTableToClipboard'
import { EsPageHeader } from './ui'
import {
  hasActivePortfolioFilters,
  intelligencePath,
  matchesProcurementTypeFilter,
  type RiskFilter,
  type SingleSourceFilter,
} from './esPortfolioFilters'
import { useEsPortfolioFilters } from './useEsPortfolioFilters'
import { buildPortfolioInsights } from './portfolioInsights'
import { ProcurementTypeSelect } from './ProcurementTypeSelect'
import { InsightStatements } from './InsightStatements'

const TOP_HEADERS = ['Name', 'Category', 'Location', 'Brand', 'Score', 'Open mitigations'] as const
const ROLLUP_HEADERS_COUNT = ['Name', 'Count', 'High', 'Avg'] as const
const ROLLUP_HEADERS_PCT = ['Name', '%', 'High %', 'Avg'] as const

function isOpenMitigation(t: SupplierMitigationTask): boolean {
  return t.status === 'open' || t.status === 'in_progress' || t.status === 'at_risk'
}

const REGION_ORDER = ['NA', 'EMEA', 'APAC', 'LATAM'] as const

function orderRegionRows(rows: RollupRow[]): RollupRow[] {
  const rank = (key: string) => {
    const i = REGION_ORDER.indexOf(key as (typeof REGION_ORDER)[number])
    return i >= 0 ? i : 100
  }
  return [...rows].sort((a, b) => {
    const ra = rank(a.key)
    const rb = rank(b.key)
    if (ra !== rb) return ra - rb
    return a.key.localeCompare(b.key)
  })
}

function collapsedToCopyRow(
  s: CollapsedSupplierRisk,
  openMitigationCount: number
): string[] {
  return [
    cell(s.name),
    cell(s.category),
    cell(s.location),
    cell(formatBrandsFull(s.brands) || null),
    s.overall_score != null ? s.overall_score.toFixed(1) : '—',
    openMitigationCount > 0 ? String(openMitigationCount) : '',
  ]
}

function matchesBrand(s: CollapsedSupplierRisk, brandFilter: string): boolean {
  if (!brandFilter) return true
  return s.brands.some((b) => b.toLowerCase() === brandFilter.toLowerCase())
}

export function ExecutiveSummaryView() {
  const { isAdmin } = useAuth()
  const { suppliers, loading, error } = useSuppliers(isAdmin ? { scope: 'all' } : undefined)
  const { tasks: mitigationTasks } = useSupplierMitigationTasks()

  const {
    filters,
    setRisk,
    setCategory,
    setSubCategory,
    setBrand,
    setRegion,
    setLocation,
    setSingleSource,
    setProcurementType,
    clearAll,
  } = useEsPortfolioFilters()
  const riskFilter = filters.risk
  const categoryFilter = filters.category
  const subCategoryFilter = filters.subCategory
  const brandFilter = filters.brand
  const regionFilter = filters.region
  const locationFilter = filters.location
  const singleSourceFilter = filters.singleSource
  const procurementTypeFilter = filters.procurementType
  const [metricMode, setMetricMode] = useState<MetricMode>('count')

  const scopedSuppliers = useMemo(
    () =>
      suppliers.filter((s) =>
        matchesProcurementTypeFilter(s.procurement_type, procurementTypeFilter)
      ),
    [suppliers, procurementTypeFilter]
  )

  const collapsedAll = useMemo(
    () => collapseSuppliersForPortfolio(scopedSuppliers, procurementTypeFilter),
    [scopedSuppliers, procurementTypeFilter]
  )

  const categories = useMemo(
    () =>
      [...new Set(collapsedAll.map((s) => s.category).filter((x): x is string => !!x))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [collapsedAll]
  )
  const regions = useMemo(
    () =>
      [
        ...new Set(
          collapsedAll
            .map((s) => s.kenvue_region)
            .filter((x): x is string => !!x && x !== 'Not Available')
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [collapsedAll]
  )
  const brands = useMemo(() => {
    const set = new Set<string>()
    for (const s of collapsedAll) {
      for (const b of s.brands) {
        if (b && b !== 'Not Available' && b !== 'NA') set.add(b)
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [collapsedAll])

  const subCategories = useMemo(() => {
    const list = collapsedAll.filter((s) => {
      const band = collapsedBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (categoryFilter && (s.category || '') !== categoryFilter) return false
      if (regionFilter && (s.kenvue_region || '') !== regionFilter) return false
      if (!collapsedMatchesLocation(s, locationFilter)) return false
      if (!matchesBrand(s, brandFilter)) return false
      if (singleSourceFilter === 'yes' && s.single_source !== true) return false
      if (singleSourceFilter === 'no' && s.single_source !== false) return false
      return true
    })
    return [
      ...new Set(
        list.map((s) => s.sub_category).filter((x): x is string => !!x && x !== 'Not Available')
      ),
    ].sort((a, b) => a.localeCompare(b))
  }, [
    collapsedAll,
    riskFilter,
    categoryFilter,
    regionFilter,
    locationFilter,
    brandFilter,
    singleSourceFilter,
  ])

  const locations = useMemo(() => {
    const list = collapsedAll.filter((s) => {
      const band = collapsedBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (categoryFilter && (s.category || '') !== categoryFilter) return false
      if (subCategoryFilter && (s.sub_category || '') !== subCategoryFilter) return false
      if (regionFilter && (s.kenvue_region || '') !== regionFilter) return false
      if (!matchesBrand(s, brandFilter)) return false
      if (singleSourceFilter === 'yes' && s.single_source !== true) return false
      if (singleSourceFilter === 'no' && s.single_source !== false) return false
      return true
    })
    const set = new Set<string>()
    for (const s of list) {
      const locs =
        s.locations.length > 0
          ? s.locations
          : (s.location || '')
              .split(',')
              .map((p) => p.trim())
              .filter((p) => p && p !== 'Not Available')
      for (const loc of locs) set.add(loc)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [
    collapsedAll,
    riskFilter,
    categoryFilter,
    subCategoryFilter,
    regionFilter,
    brandFilter,
    singleSourceFilter,
  ])

  useEffect(() => {
    if (subCategoryFilter && !subCategories.includes(subCategoryFilter)) {
      setSubCategory('')
    }
  }, [subCategories, subCategoryFilter, setSubCategory])

  useEffect(() => {
    if (locationFilter && !locations.includes(locationFilter)) {
      setLocation('')
    }
  }, [locations, locationFilter, setLocation])

  const filteredCollapsed = useMemo(() => {
    return collapsedAll.filter((s) => {
      const band = collapsedBand(s)
      if (riskFilter === 'unscored') {
        if (band != null) return false
      } else if (riskFilter !== 'all' && band !== riskFilter) return false
      if (categoryFilter && (s.category || '') !== categoryFilter) return false
      if (subCategoryFilter && (s.sub_category || '') !== subCategoryFilter) return false
      if (regionFilter && (s.kenvue_region || '') !== regionFilter) return false
      if (!collapsedMatchesLocation(s, locationFilter)) return false
      if (!matchesBrand(s, brandFilter)) return false
      if (singleSourceFilter === 'yes' && s.single_source !== true) return false
      if (singleSourceFilter === 'no' && s.single_source !== false) return false
      return true
    })
  }, [
    collapsedAll,
    riskFilter,
    categoryFilter,
    subCategoryFilter,
    regionFilter,
    locationFilter,
    brandFilter,
    singleSourceFilter,
  ])

  const stats = useMemo(() => {
    const collapsed = filteredCollapsed
    const scored = collapsed.filter((s) => s.overall_score != null)
    const bands = bandCountsUnique(collapsed)
    return {
      ...bands,
      topSuppliers: scored.slice(0, 10),
      topSuppliersAll: scored,
      byCategory: rollupCollapsedByAttribute(collapsed, collapsedCategoryKey),
      bySubCategory: rollupCollapsedByAttribute(collapsed, collapsedSubCategoryKey),
      byBrand: rollupCollapsedByBrand(collapsed),
      byLocation: rollupCollapsedByLocation(collapsed),
      byRegion: orderRegionRows(rollupCollapsedByAttribute(collapsed, collapsedRegionKey)),
    }
  }, [filteredCollapsed])

  const openMitigationCountBySupplierId = useMemo(() => {
    const map = new Map<string, number>()
    for (const t of mitigationTasks) {
      if (!t.supplier_id || !isOpenMitigation(t)) continue
      map.set(t.supplier_id, (map.get(t.supplier_id) || 0) + 1)
    }
    return map
  }, [mitigationTasks])

  const openCountFor = (ids: string[]): number => {
    let n = 0
    for (const id of ids) n += openMitigationCountBySupplierId.get(id) || 0
    return n
  }

  const insights = useMemo(() => {
    const highRisk = filteredCollapsed.filter((s) => collapsedBand(s) === 'high')
    let highWithOpenMitigation = 0
    for (const s of highRisk) {
      if (openCountFor(s.productLineIds) > 0) highWithOpenMitigation++
    }
    return buildPortfolioInsights({
      collapsed: filteredCollapsed,
      highWithOpenMitigation,
      emphasizeCountry: false,
    })
  }, [filteredCollapsed, openMitigationCountBySupplierId])

  const topRowsAll = useMemo(
    () =>
      stats.topSuppliersAll.map((s) => collapsedToCopyRow(s, openCountFor(s.productLineIds))),
    [stats.topSuppliersAll, openMitigationCountBySupplierId]
  )

  const selectClass =
    'rounded-[var(--radius-sm)] border border-es-hairline px-2.5 py-2 text-sm bg-es-white text-es-body focus:border-es-signal focus:outline-none focus:ring-1 focus:ring-es-signal'

  return (
    <div className="p-4 md:p-6 space-y-6">
      <EsPageHeader
        className="!mb-3"
        title="Risk overview"
        description="Executive snapshot: key insights and exceptions across the portfolio. Use Intelligence view to explore suppliers in detail, and Risk & mitigation monitor for open mitigation activity."
      />
      <ProcurementTypeSelect
        value={procurementTypeFilter}
        onChange={setProcurementType}
        className="-mt-2"
      />
      <RiskScaleLegend />

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">
          {toErrorMessage(error, 'Failed to load data.')}
        </div>
      )}

      {loading ? (
        <div className="text-es-muted text-sm">Loading...</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={riskFilter}
              onChange={(e) => setRisk(e.target.value as RiskFilter)}
              className={selectClass}
              aria-label="Risk level"
            >
              <option value="all">Risk Level</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="unscored">Unscored</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategory(e.target.value)}
              className={selectClass}
              aria-label="Category"
            >
              <option value="">Category</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={subCategoryFilter}
              onChange={(e) => setSubCategory(e.target.value)}
              className={selectClass}
              aria-label="Sub-category"
            >
              <option value="">Sub-category</option>
              {subCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={brandFilter}
              onChange={(e) => setBrand(e.target.value)}
              className={selectClass}
              aria-label="Brand"
            >
              <option value="">Brand</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegion(e.target.value)}
              className={selectClass}
              aria-label="Region"
            >
              <option value="">Region</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <select
              value={locationFilter}
              onChange={(e) => setLocation(e.target.value)}
              className={selectClass}
              aria-label="Country"
            >
              <option value="">Country</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
            <select
              value={singleSourceFilter}
              onChange={(e) => setSingleSource(e.target.value as SingleSourceFilter)}
              className={selectClass}
              aria-label="Single source"
            >
              <option value="all">Single-source: All</option>
              <option value="yes">Single-source: Yes</option>
              <option value="no">Single-source: No</option>
            </select>
            {hasActivePortfolioFilters(filters) && (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-semibold text-es-signal hover:text-es-deep hover:underline px-1"
              >
                Clear filters
              </button>
            )}
          </div>

          {procurementTypeFilter === 'indirect' && stats.unscored > 0 && (
            <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2">
              {stats.unscored} unique Indirect supplier{stats.unscored === 1 ? '' : 's'} have no risk
              scores (master rows kept from import — expected on the Indirect workbook).
            </p>
          )}

          <InsightStatements insights={insights} title="Key insights" />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-es-muted">
              Supporting metrics · High + Medium + Low + Unscored ={' '}
              {stats.high + stats.medium + stats.low + stats.unscored} (matches {stats.total} unique
              suppliers). Count = unique suppliers except By brand. Missing dimensions appear as
              Unclassified.
              {metricMode === 'pct'
                ? ' Band % sum to 100%. Rollup % is of the filtered set; High % is within each group.'
                : ''}
            </p>
            <MetricModeToggle value={metricMode} onChange={setMetricMode} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(() => {
              const bandPct =
                metricMode === 'pct'
                  ? formatExclusivePercents([
                      stats.high,
                      stats.medium,
                      stats.low,
                      stats.unscored,
                    ])
                  : null
              return (
              [
                {
                  label: 'Suppliers',
                  value: formatMetric(stats.total, stats.total, metricMode),
                  className: 'text-es-navy',
                  to: null as string | null,
                },
                {
                  label: 'High risk',
                  value: bandPct ? bandPct[0] : String(stats.high),
                  className: 'text-es-riskHigh',
                  to: intelligencePath(filters, { risk: 'high' }),
                },
                {
                  label: 'Medium risk',
                  value: bandPct ? bandPct[1] : String(stats.medium),
                  className: 'text-es-riskMedium',
                  to: intelligencePath(filters, { risk: 'medium' }),
                },
                {
                  label: 'Low risk',
                  value: bandPct ? bandPct[2] : String(stats.low),
                  className: 'text-es-riskLow',
                  to: intelligencePath(filters, { risk: 'low' }),
                },
                {
                  label: 'Unscored',
                  value: bandPct ? bandPct[3] : String(stats.unscored),
                  className: 'text-es-muted',
                  to: intelligencePath(filters, { risk: 'unscored' }),
                },
              ] as const
            )
            })().map((k) => {
              const body = (
                <>
                  <div className={`text-2xl font-bold tabular-nums ${k.className}`}>{k.value}</div>
                  <div className="text-xs font-semibold text-es-muted uppercase tracking-wider">{k.label}</div>
                </>
              )
              if (k.to) {
                return (
                  <Link
                    key={k.label}
                    to={k.to}
                    className="rounded-[var(--radius-lg)] px-4 py-3 shadow-[var(--shadow-card)] border border-es-soft bg-es-white text-left transition-colors hover:border-es-signal hover:bg-es-haze/60 block"
                  >
                    {body}
                  </Link>
                )
              }
              return (
                <div
                  key={k.label}
                  className="rounded-[var(--radius-lg)] px-4 py-3 shadow-[var(--shadow-card)] border border-es-soft bg-es-white"
                >
                  {body}
                </div>
              )
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RiskBandChart
              bands={{
                high: stats.high,
                medium: stats.medium,
                low: stats.low,
                unscored: stats.unscored,
                total: stats.total,
              }}
              title="Risk distribution"
              onBandClick={(band) => setRisk(band)}
            />
            <RollupBarsChart
              title="Top categories by high risk"
              rows={stats.byCategory}
              valueKey="high"
              portfolioTotal={stats.total}
              onRowClick={(key) => setCategory(key)}
            />
            <RollupBarsChart
              title="Regions by supplier count"
              rows={stats.byRegion}
              valueKey="count"
              portfolioTotal={stats.total}
              onRowClick={(key) => setRegion(key)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <SummaryTile
              title="Top risk suppliers"
              headerAction={
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <CopyTableButtons
                    payload={{ headers: [...TOP_HEADERS], rows: topRowsAll }}
                  />
                  <Link
                    to={intelligencePath(filters)}
                    className="text-xs font-semibold text-es-signal hover:text-es-deep hover:underline"
                  >
                    Open Intelligence view
                  </Link>
                </div>
              }
              footer="Top 10 unique suppliers by score (brands combined) · Copy = all scored"
            >
              {stats.topSuppliers.length === 0 ? (
                <p className="text-sm text-es-muted py-4">No scored suppliers yet.</p>
              ) : (
                <ul className="divide-y divide-es-soft">
                  {stats.topSuppliers.map((s) => {
                    const brandsLabel = formatBrandsDisplay(s.brands)
                    const openN = openCountFor(s.productLineIds)
                    const linkId =
                      s.productLineIds.find((id) => (openMitigationCountBySupplierId.get(id) || 0) > 0) ??
                      s.productLineIds[0]
                    const monitorHref =
                      linkId != null
                        ? `/early-signals/dashboard?supplier=${encodeURIComponent(linkId)}`
                        : '/early-signals/dashboard'
                    return (
                      <li key={s.key} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <Link
                            to={intelligencePath(filters, {
                              supplier: s.productLineIds[0] || null,
                            })}
                            className="font-medium text-es-navy truncate block hover:text-es-signal hover:underline"
                            title="Open supplier profile in Intelligence view"
                          >
                            {s.name}
                          </Link>
                          <div className="text-xs text-es-muted truncate">
                            {[s.category, s.location, brandsLabel].filter(Boolean).join(' · ')}
                          </div>
                          {openN > 0 && (
                            <Link
                              to={monitorHref}
                              className="inline-flex mt-1 text-[11px] font-semibold text-es-signal hover:text-es-deep hover:underline"
                              title="Open Risk & mitigation monitor for this supplier"
                            >
                              {openN} open mitigation{openN === 1 ? '' : 's'}
                            </Link>
                          )}
                        </div>
                        <span className="font-bold text-es-riskHigh tabular-nums shrink-0">
                          {s.overall_score != null ? s.overall_score.toFixed(1) : '—'}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </SummaryTile>
            <RollupCard
              title="By category"
              rows={stats.byCategory}
              countLabel="Unique suppliers"
              metricMode={metricMode}
              denom={stats.total}
              onRowClick={(key) => setCategory(key === 'Unclassified' ? '' : key)}
            />
            <RollupCard
              title="By sub-category"
              rows={stats.bySubCategory}
              countLabel="Unique suppliers"
              metricMode={metricMode}
              denom={stats.total}
              onRowClick={(key) => setSubCategory(key === 'Unclassified' ? '' : key)}
            />
            <RollupCard
              title="By region"
              rows={stats.byRegion}
              countLabel="Unique suppliers"
              metricMode={metricMode}
              denom={stats.total}
              onRowClick={(key) => setRegion(key === 'Unclassified' ? '' : key)}
            />
            <RollupCard
              title="By country"
              rows={stats.byLocation}
              countLabel="Unique suppliers"
              metricMode={metricMode}
              denom={stats.total}
              onRowClick={(key) => setLocation(key === 'Unclassified' ? '' : key)}
            />
            <RollupCard
              title="By brand"
              rows={stats.byBrand}
              countLabel="Supplier–brand lines"
              metricMode={metricMode}
              denom={stats.byBrand.reduce((n, r) => n + r.count, 0) || 1}
              onRowClick={(key) => setBrand(key === 'Unclassified' ? '' : key)}
            />
          </div>
        </>
      )}
    </div>
  )
}

function SummaryTile({
  title,
  headerAction,
  footer,
  children,
}: {
  title: string
  headerAction?: ReactNode
  footer?: string
  children: ReactNode
}) {
  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 flex flex-col h-[25rem]">
      <div className="flex items-center justify-between gap-2 mb-3 shrink-0 min-h-[1.25rem]">
        <h2 className="text-sm font-bold text-es-navy uppercase tracking-wider">{title}</h2>
        {headerAction}
      </div>
      <div className="h-72 shrink-0 overflow-y-auto overscroll-contain">{children}</div>
      <p className="text-[11px] text-es-muted mt-2 shrink-0 min-h-[1rem]">{footer ?? '\u00a0'}</p>
    </section>
  )
}

function RollupCard({
  title,
  rows,
  countLabel = 'Unique suppliers',
  metricMode = 'count',
  denom,
  onRowClick,
}: {
  title: string
  rows: RollupRow[]
  countLabel?: string
  metricMode?: MetricMode
  /** Denominator for % mode (filtered unique suppliers, or brand-line sum). */
  denom: number
  onRowClick?: (key: string) => void
}) {
  const supplierSum = rows.reduce((n, r) => n + r.count, 0)
  const headers = metricMode === 'pct' ? ROLLUP_HEADERS_PCT : ROLLUP_HEADERS_COUNT
  const tableRows = rows.map((r) => [
    cell(r.key),
    formatMetric(r.count, denom, metricMode),
    formatMetric(r.high, metricMode === 'pct' ? Math.max(r.count, 1) : denom, metricMode),
    r.avg != null ? r.avg.toFixed(1) : '—',
  ])
  return (
    <SummaryTile
      title={title}
      headerAction={
        <CopyTableButtons
          payload={{ headers: [...headers], rows: tableRows }}
        />
      }
      footer={
        rows.length === 0
          ? undefined
          : `${rows.length} groups · ${supplierSum} ${countLabel.toLowerCase()} (includes Unclassified)`
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-es-muted">No data.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-es-white z-[1]">
              <tr className="bg-es-haze border-b border-es-soft text-xs text-es-muted uppercase tracking-wider text-left">
                <th className="pb-2 pt-1 px-1 font-semibold">Name</th>
                <th
                  className="pb-2 pt-1 px-1 font-semibold text-right"
                  title={metricMode === 'pct' ? `% of ${countLabel.toLowerCase()}` : countLabel}
                >
                  {metricMode === 'pct' ? '%' : 'Count'}
                </th>
                <th
                  className="pb-2 pt-1 px-1 font-semibold text-right"
                  title={
                    metricMode === 'pct'
                      ? 'Share of this group that is high risk'
                      : 'High-risk suppliers in group'
                  }
                >
                  {metricMode === 'pct' ? 'High %' : 'High'}
                </th>
                <th className="pb-2 pt-1 px-1 font-semibold text-right" title="Mean of scored unique suppliers">
                  Avg
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-es-soft">
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className={onRowClick ? 'cursor-pointer hover:bg-es-haze/50' : undefined}
                  onClick={onRowClick ? () => onRowClick(r.key) : undefined}
                  title={onRowClick ? `Filter to ${r.key}` : undefined}
                >
                  <td className="py-1.5 text-es-navy font-medium max-w-[180px] truncate">
                    {onRowClick ? (
                      <span className="text-es-signal underline-offset-2 hover:underline">{r.key}</span>
                    ) : (
                      r.key
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-es-body">
                    {formatMetric(r.count, denom, metricMode)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-es-riskHigh font-medium">
                    {formatMetric(
                      r.high,
                      metricMode === 'pct' ? Math.max(r.count, 1) : denom,
                      metricMode
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-es-body">
                    {r.avg != null ? r.avg.toFixed(1) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SummaryTile>
  )
}
